import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Tag, Input, DatePicker } from "antd";
import styled from "styled-components";
import { Container } from "../../components/atoms/container/Container";
import PaginationControls from "../../components/atoms/pagination/Pagination";
import { getAmbulanceTrips } from "../../http";
import style from "./ambulanceTrips.module.css";

const { RangePicker } = DatePicker;
const PAGE_SIZE = 20;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    font-size: 1.25rem; font-family: 'Poppins', sans-serif;
    font-weight: 500 !important; height: 4.5rem; letter-spacing: 1.32px;
  }
  .ant-table-tbody > tr > td {
    font-size: 1.125rem; font-family: 'Poppins', sans-serif;
    font-weight: 400 !important; letter-spacing: 1.32px; cursor: pointer;
  }
  @media (max-width: 768px) {
    .ant-table-tbody > tr > td, .ant-table-thead > tr > th { font-size: 1rem; height: 3.5rem; }
    .ant-table-cell { padding: 5px !important; }
  }
`;

export const AmbulanceTripsPage = () => {
  const navigate = useNavigate();
  const [rows, setRows]       = useState<any[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);

  const [ticketSearchInput, setTicketSearchInput] = useState("");
  const [ticketSearch, setTicketSearch]           = useState("");
  const [dateRange, setDateRange]                 = useState<[string, string] | null>(null);
  const [pageNumber, setPageNumber]               = useState(1);
  const [pageSize, setPageSize]                   = useState(PAGE_SIZE);

  // Debounce ticket search
  useEffect(() => {
    const timer = setTimeout(() => { setTicketSearch(ticketSearchInput); setPageNumber(1); }, 400);
    return () => clearTimeout(timer);
  }, [ticketSearchInput]);

  const fetchTrips = async () => {
    setLoading(true);
    try {
      const res = await getAmbulanceTrips({
        ticketId: ticketSearch || undefined,
        fromDate: dateRange?.[0],
        toDate:   dateRange?.[1],
        page:     pageNumber,
        pageSize,
      });
      setRows(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
    } catch { setRows([]); setTotal(0); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTrips(); }, [ticketSearch, dateRange, pageNumber, pageSize]);

  const clearAllFilters = () => {
    setTicketSearchInput(""); setTicketSearch("");
    setDateRange(null); setPageNumber(1);
  };

  const columns = [
    { title: "Ticket",  dataIndex: "ticketId",  key: "ticketId",  render: (v: string) => v ?? "—" },
    { title: "Patient", dataIndex: "patient",   key: "patient",   render: (p: any) => p?.name ?? "—" },
    {
      title: "Pickup", dataIndex: "pickupLocation", key: "pickup",
      render: (l: any) => {
        const addr = l?.address ?? "—";
        return <span title={addr} style={{ display: "block", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{addr}</span>;
      },
    },
    {
      title: "Drop", dataIndex: "dropLocation", key: "drop",
      render: (drops: any[]) => {
        if (!drops?.length) return "—";
        const last    = drops[drops.length - 1]?.address || "—";
        const display = drops.length > 1 ? `${last} (+${drops.length - 1} more)` : last;
        return <span title={last} style={{ display: "block", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{display}</span>;
      },
    },
    {
      title: "Providers", dataIndex: "assignmentCount", key: "providers",
      render: (count: number) => (
        <Tag style={{ background: "#e6f4ff", color: "#1677ff", border: "none", fontFamily: "Poppins" }}>
          {count ?? 0}
        </Tag>
      ),
    },
    {
      title: "Agent", dataIndex: "createdBy", key: "agent",
      render: (a: any) => a?.agentName
        ? <span style={{ fontFamily: "Poppins", fontSize: "0.9rem" }}>{a.agentName}</span>
        : <span style={{ color: "#bbb" }}>—</span>,
    },
    {
      title: "Created", dataIndex: "createdAt", key: "createdAt",
      render: (v: string) => v ? new Date(v).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—",
    },
  ];

  return (
    <div>
      <Container className={style.wrapper}>
        <Container className={style.container}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", alignItems: "center" }}>
            <Input
              allowClear
              placeholder="Search by Ticket ID"
              value={ticketSearchInput}
              onChange={(e) => setTicketSearchInput(e.target.value)}
              style={{ width: 220, height: "3.563rem", fontFamily: "Poppins" }}
            />
            <RangePicker
              style={{ height: "3.563rem", fontFamily: "Poppins" }}
              format="DD MMM YYYY"
              onChange={(_, strings) => {
                setDateRange(strings[0] && strings[1] ? [strings[0], strings[1]] : null);
                setPageNumber(1);
              }}
            />
            <button className={style.filterBtn} onClick={clearAllFilters}>Clear All</button>
          </div>
        </Container>
      </Container>

      <Container className={style.table}>
        <StyledTable
          loading={loading}
          dataSource={rows}
          columns={columns}
          pagination={false}
          rowKey="_id"
          onRow={(record: any) => ({ onClick: () => navigate(`/ambulance-trips/${record._id}`) })}
          locale={{ emptyText: "No ambulance trips found" }}
        />
      </Container>

      <PaginationControls
        pageNumber={pageNumber} pageSize={pageSize} totalDataCount={total}
        handlePageSizeChange={(size) => { setPageSize(size); setPageNumber(1); }}
        handleDataChange={(page) => setPageNumber(page)}
      />
    </div>
  );
};
